// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Checkbox,
  RadioGroup,
  Radio,
  Text,
} from 'informed';

import {
  addHIVTest,
  deleteHIVTest,
  updateHIVTest,
  updateAdmissionHIV0Test,
  updateAdmissionHIV6Test,
} from '../../../actions/testingActions';

import { Button } from '../../Buttons.jsx';
import DateText from '../../DateText.jsx';
import { Error } from '../../Typography.jsx';
import Table from '../../Table.jsx';
import ValidateText from '../../ValidateText.jsx';

type Props = {
  admissionHivTest0: Object,
  admissionHivTest6: Object,
  wellChildChecks: Object[],
  dateArrived: string,
  hivTests: Object[],
  activePatientId: string,
  addHIVTest: Function,
  deleteHIVTest: Function,
  updateHIVTest: Function,
  updateAdmissionHIV0Test: Function,
  updateAdmissionHIV6Test: Function,
}

class HIVTestsComponent extends Component<Props> {
  DATE_OF_ADMISSION_TEXT_0 = 'Date of admission';

  DATE_OF_ADMISSION_TEXT_6 = '6 weeks from admission';

  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY').toISOString();
    // For admission tests, [dateDue] is a special string rather than a date,
    // which must be kept intact when sent to the server.
    if (formValues.dateDue !== this.DATE_OF_ADMISSION_TEXT_0 && formValues.dateDue !== this.DATE_OF_ADMISSION_TEXT_6) {
      const dateDue = moment(formValues.dateDue, 'DD-MM-YYYY').toISOString();
      formValues.dateDue = dateDue;
    }
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteHIVTest(this.props.activePatientId, id);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    if (formData.values.dateDue === this.DATE_OF_ADMISSION_TEXT_0) {
      this.props.updateAdmissionHIV0Test(this.props.activePatientId, {
        testId: id,
        ...formData.values,
      });
    } else if (formData.values.dateDue === this.DATE_OF_ADMISSION_TEXT_6) {
      this.props.updateAdmissionHIV6Test(this.props.activePatientId, {
        testId: id,
        ...formData.values,
      });
    } else {
      this.props.updateHIVTest(this.props.activePatientId, {
        testId: id,
        ...formData.values,
      });
    }
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addHIVTest(this.props.activePatientId, formData.values);
  }

  showDelete = (row: Object) => !row.admissionTest && !row.fromWcc;

  showEdit = (row: Object) => !row.fromWcc;

  // overdue if no date and exists due date earlier than now
  isOverdue = item => (!item.date
    && !item.overridden
    && item.dateDue
    && moment(item.dateDue) < moment())

  render() {
    let { admissionHivTest0, admissionHivTest6 } = this.props;
    const admissionHivTests = [];
    if (this.props.dateArrived) {
      if (admissionHivTest0) {
        admissionHivTest0 = {
          admissionTest: '0',
          dateDue: moment(this.props.dateArrived).startOf('day').toISOString(),
          ...admissionHivTest0,
        };
        admissionHivTests.push(admissionHivTest0);
      }
      if (admissionHivTest6) {
        admissionHivTest6 = {
          admissionTest: '6',
          dateDue: moment(this.props.dateArrived).add(6, 'weeks').startOf('day').toISOString(),
          ...admissionHivTest6,
        };
        admissionHivTests.push(admissionHivTest6);
      }
    }
    const wccHivTests = this.props.wellChildChecks
      .filter(wcc => wcc.vitals.hivTest)
      .map(wcc => ({
        date: wcc.date,
        type: 'Rapid',
        positive: wcc.vitals.hivTest,
        fromWcc: true,
      }));

    let allHivTests = wccHivTests.concat(admissionHivTests).concat(this.props.hivTests);
    allHivTests.sort((a, b) => {
      // overdues go first
      const aOverdue = this.isOverdue(a);
      const bOverdue = this.isOverdue(b);
      if (!aOverdue && bOverdue) {
        return 1;
      }
      if (!bOverdue && aOverdue) {
        return -1;
      }

      // otherwise based on date due or date administered if date due DNE
      let aDate = a.date;
      if (!aDate && !a.overridden) {
        aDate = a.dateDue;
      }
      let bDate = b.date;
      if (!bDate && !b.overridden) {
        bDate = b.dateDue;
      }
      if (!aDate) {
        return 1;
      }
      if (!bDate) {
        return -1;
      }
      if (aDate < bDate) {
        return 1;
      }
      if (bDate < aDate) {
        return -1;
      }
      return 0;
    });
    allHivTests = allHivTests.map((item, index) => ({ testNum: allHivTests.length - index, ...item }));

    const columns = [
      {
        Header: '',
        id: 'index',
        cell: item => `Test #${item.testNum || ''}`,
        width: 80,
      },
      {
        Header: 'Date',
        id: 'date',
        cell: (item) => {
          if (item.overridden) {
            return '(overridden)';
          }
          // overdue if no administered date and if due date earlier than now
          const overdue = !item.date && item.dateDue && moment(item.dateDue) < moment();
          return overdue ? <Error>-- (overdue)</Error>
            : item.date ? moment(item.date).format() : '--';
        },
        editCell: item => <div>
          <DateText field='date' item={item} initEmpty={item.overridden} />
          {item.admissionTest && <div>
            <br />
            <Checkbox field='overridden' initialValue={item.overridden} />Override
          </div>}
        </div>,
      },
      {
        Header: 'Type',
        id: 'type',
        editCell: item => (
          <RadioGroup field='type' initialValue={item.type}>
            <Radio value={'Rapid'} />Rapid
            <br />
            <Radio value={'PCR'} />PCR
          </RadioGroup>
        ),
      },
      {
        Header: 'Date Due',
        id: 'dateDue',
        cell: (item) => {
          if (item.fromWcc) {
            return '(from WCC)';
          }
          return item.dateDue ? moment(item.dateDue).format() : '--';
        },
        editCell: (item) => {
          switch (item.admissionTest) {
            case '0':
              return <Text field='dateDue' initialValue={this.DATE_OF_ADMISSION_TEXT_0} disabled />;
            case '6':
              return <Text field='dateDue' initialValue={this.DATE_OF_ADMISSION_TEXT_6} disabled />;
            default:
              return <DateText field='dateDue' item={item} />;
          }
        },
      },
      {
        Header: 'Result',
        id: 'positive',
        editCell: item => (
          <RadioGroup field='positive' initialValue={item.positive}>
            <Radio value={'Positive'} />Positive
            <br />
            <Radio value={'Negative'} />Negative
            <br />
            <Radio value={'Unknown'} />Unknown
          </RadioGroup>
        ),
      },
      {
        Header: 'Link',
        id: 'link',
        cell: (item) => {
          if (!item.link) {
            return '--';
          }
          let { link } = item;
          if (link.indexOf('http') === -1) {
            link = `http://${link}`;
          }
          return <a target='_blank' rel='noopener noreferrer' href={link}><Button>Open Link</Button></a>;
        },
        editCell: item => <ValidateText
          field='link'
          initialValue={item.link}
          placeholder='e.g. Google Drive link'
          regex={/^(https?:\/\/.*)?$/}
          errorText='Improper URL, please use http://...'
        />,
        width: 150,
      },
    ];

    return (
      <Table
        columns={columns}
        data={allHivTests}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
        handleSaveNewRow={this.handleSaveNewRow}
        showDelete={this.showDelete}
        showEdit={this.showEdit}
      />
    );
  }
}

const mapStateToProps = state => ({
  admissionHivTest0: state.patient.data.testing.admissionHivTest0,
  admissionHivTest6: state.patient.data.testing.admissionHivTest6,
  hivTests: state.patient.data.testing.hivTests,
  activePatientId: state.patient.data.activePatientId,
  dateArrived: state.patient.data.generalInfo.dateArrived,
  wellChildChecks: state.patient.data.wellChildChecks,
});
const mapDispatchToProps = dispatch => ({
  addHIVTest:
    (patientId, hivTest) => dispatch(addHIVTest(patientId, hivTest)),
  deleteHIVTest:
    (patientId, hivTestId) => dispatch(deleteHIVTest(patientId, hivTestId)),
  updateHIVTest:
    (patientId, hivTest) => dispatch(updateHIVTest(patientId, hivTest)),
  updateAdmissionHIV0Test:
    (patientId, hivTest) => dispatch(updateAdmissionHIV0Test(patientId, hivTest)),
  updateAdmissionHIV6Test:
    (patientId, hivTest) => dispatch(updateAdmissionHIV6Test(patientId, hivTest)),
});

const HIVTests = connect(
  mapStateToProps,
  mapDispatchToProps,
)(HIVTestsComponent);

export default HIVTests;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/testing/HIVTests.jsx